import {
  appendQuery,
  buildCellServiceUrl,
  type CellServiceQuery,
} from './cell-session';
import {
  buildSessionsFromHistoryItems,
  shouldReadLegacyDisplayNamePartition,
} from './chat-archive-sessions';
import { BROWSER_WAF_BODY_LIMIT, prepareGzipJsonBody } from './cors-json-body-compression';
import { isAbortError, withTokenAuthRecovery } from './cors-auth-recovery';
import { getTokenInfo } from './functions';

/** Path prefix for every assistant-service endpoint. */
const API_PREFIX = '/assistant-service';

const CELL_MODE_OMIT_HEADERS = new Set([
  'chat-history-table-name',
  'chat-history-archive-table-name',
  'threat-reports-table-name',
  'chat-answers-bucket-name',
  'unified-bucket-name',
  'processed-bucket-name',
]);

export class AssistantApiError extends Error {
  public readonly status: number | null;
  public readonly detail: string | null;

  constructor(message: string, status: number | null = null, detail: string | null = null) {
    super(message);
    this.name = 'AssistantApiError';
    this.status = status;
    this.detail = detail;
  }
}

export function buildAssistantUrl(cellId: string, path: string): string {
  return buildCellServiceUrl(cellId, API_PREFIX, path);
}

export type AssistantQuery = CellServiceQuery;

type AssistantSessionArgs = {
  cellId: string;
  accessToken: string;
  idToken?: string | null;
};

type AssistantRequestArgs = AssistantSessionArgs & {
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: AssistantQuery;
  signal?: AbortSignal;
  timeoutMs?: number;
};

function assertSession(cellId: string, accessToken: string): void {
  if (!cellId) {
    throw new AssistantApiError('Missing cellId (custom:cell_id claim) on the ID token.');
  }
  if (!accessToken) {
    throw new AssistantApiError('Missing access token.');
  }
}

function resolveTenantId(
  accessTokenInfo: Record<string, unknown> | null,
  idTokenInfo: Record<string, unknown> | null,
): string {
  return (
    (accessTokenInfo?.['custom:tenant'] as string | undefined) ||
    (accessTokenInfo?.['custom:tenant_id'] as string | undefined) ||
    (idTokenInfo?.['custom:tenant_id'] as string | undefined) ||
    (idTokenInfo?.['custom:tenant'] as string | undefined) ||
    ''
  );
}

function buildAssistantClientHeaders(args: AssistantSessionArgs): Record<string, string> {
  const idTokenInfo = (args.idToken ? getTokenInfo(args.idToken) : null) as Record<string, unknown> | null;
  const accessTokenInfo = getTokenInfo(args.accessToken) as Record<string, unknown> | null;
  const tenantId = resolveTenantId(accessTokenInfo, idTokenInfo);
  const tenantName =
    (idTokenInfo?.['custom:tenant_name'] as string | undefined) ||
    (accessTokenInfo?.['custom:tenant_name'] as string | undefined) ||
    null;
  const cellId =
    String((idTokenInfo?.['custom:cell_id'] as string | undefined) || '').trim() ||
    String((accessTokenInfo?.['custom:cell_id'] as string | undefined) || '').trim() ||
    args.cellId;
  const environment =
    String((idTokenInfo?.['custom:env'] as string | undefined) || '').trim() ||
    String((accessTokenInfo?.['custom:env'] as string | undefined) || '').trim() ||
    (cellId ? cellId.split('-')[1] : 'dev');

  const headers: Record<string, string> = {
    'organization-id': tenantId,
    'system-stage': environment || 'dev',
    'product-shorthand': 'tpl',
    'system-identifier': tenantId,
    'bkb-id': 'default',
    'tenant-id': tenantId,
  };
  if (tenantName) {
    headers['tenant-name'] = tenantName;
  }
  if (cellId) {
    for (const key of CELL_MODE_OMIT_HEADERS) {
      delete headers[key];
    }
  }
  return headers;
}

async function readErrorAndThrow(res: Response, path: string): Promise<never> {
  const detail = await res.text().catch(() => '');
  throw new AssistantApiError(
    `assistant-service ${path} returned ${res.status}`,
    res.status,
    detail || null,
  );
}

export async function assistantRequest<T>(args: AssistantRequestArgs): Promise<T> {
  const { cellId, idToken, path, method, body, query, signal, timeoutMs = 120_000 } = args;
  assertSession(cellId, args.accessToken);

  const url = appendQuery(buildAssistantUrl(cellId, path), query);
  // Body prep is token-independent — do it once, not per auth-retry attempt.
  const { body: outboundBody, extraHeaders, meta } = await prepareGzipJsonBody(body);
  const hasBody = outboundBody !== undefined;

  if (hasBody) {
    // Request-size diagnostics. The dev edge-WAF rejects bodies over ~8 KB, so a
    // raw payload above the limit that did NOT gzip (e.g. it compressed poorly,
    // or CompressionStream is unavailable) is the prime suspect when a POST
    // silently never reaches the assistant service.
    const sizeMsg =
      `assistant-service ${method} ${path} request size: ` +
      `raw=${meta.rawBytes}B sent=${meta.sentBytes}B ` +
      `gzipped=${meta.gzipped} overWafLimit(${BROWSER_WAF_BODY_LIMIT}B)=${meta.exceededWafLimit}`;
    if (meta.exceededWafLimit && !meta.gzipped) {
      console.warn(`[assistant-size] ${sizeMsg} — body exceeds WAF limit and was sent uncompressed`);
    } else {
      console.debug(`[assistant-size] ${sizeMsg}`);
    }
  }

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...buildAssistantClientHeaders({ cellId, accessToken, idToken }),
          ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
          ...extraHeaders,
        },
        body: outboundBody,
        signal: signal ?? AbortSignal.timeout(timeoutMs),
      });
    } catch (error) {
      // A caller-cancelled request is not a failure. Wrapping it would match
      // isLikelyCorsOrNetworkFailure and trigger a token refresh for a cancel.
      if (isAbortError(error)) throw error;
      throw new AssistantApiError(
        `Network error calling assistant-service ${path}: ${(error as Error).message}`,
      );
    }

    if (!res.ok) {
      await readErrorAndThrow(res, path);
    }

    if (res.status === 204) {
      return null as unknown as T;
    }

    return (await res.json().catch(() => null)) as T;
  });
}

export type PostAssistantChatArgs = AssistantSessionArgs & {
  body: Record<string, unknown>;
  threatReport?: boolean;
  signal?: AbortSignal;
};

export async function postAssistantChat(args: PostAssistantChatArgs): Promise<Record<string, unknown>> {
  return assistantRequest<Record<string, unknown>>({
    ...args,
    path: '/chat',
    method: 'POST',
    body: args.body,
  });
}

export type ChatHistoryMutationArgs = AssistantSessionArgs & {
  body: Record<string, unknown>;
  signal?: AbortSignal;
};

function historyBodyCaseId(body: Record<string, unknown>): string {
  return typeof body.caseId === 'string' ? body.caseId : '';
}

function historyBodyCaseFolderName(body: Record<string, unknown>): string {
  return typeof body.caseFolderName === 'string' ? body.caseFolderName : '';
}

function withoutCaseFolderName(body: Record<string, unknown>): Record<string, unknown> {
  const next = { ...body };
  delete next.caseFolderName;
  return next;
}

function historyRowIdentity(item: unknown): string | null {
  if (!item || typeof item !== 'object') return null;
  const row = item as Record<string, unknown>;
  return `${String(row.chat_key ?? '')}|${String(row.session_id ?? '')}|${String(row.epoch_time_secs ?? '')}`;
}

function historyRowEpoch(item: unknown): number | null {
  if (!item || typeof item !== 'object') return null;
  const raw = (item as Record<string, unknown>).epoch_time_secs;
  const epoch = typeof raw === 'string' ? Number.parseFloat(raw) : Number(raw);
  return Number.isFinite(epoch) ? epoch : null;
}

/**
 * Order merged rows oldest-first, the way a single-partition response already
 * arrives.
 *
 * Chat loaders treat the array as one chronological thread and take the active
 * session from the last row (``commitAnswers`` in ``chat.tsx``). Appending the
 * older display-name turns after the folder-token turns therefore rendered newer
 * replies above the prior conversation and reset ``sessionId`` to the pre-token
 * session, so the next send kept writing to the old thread (TR-361).
 *
 * A row with no usable ``epoch_time_secs`` has no defined place in that order,
 * so the concatenation is left as-is rather than sorted against a guess.
 */
function sortHistoryRowsByEpoch(rows: unknown[]): unknown[] {
  const keyed: { row: unknown; epoch: number }[] = [];
  for (const row of rows) {
    const epoch = historyRowEpoch(row);
    if (epoch === null) return rows;
    keyed.push({ row, epoch });
  }
  // Stable, so turns sharing an epoch keep their partition order.
  keyed.sort((a, b) => a.epoch - b.epoch);
  return keyed.map((entry) => entry.row);
}

/** Union two history lists without duplicating the same turn. */
export function mergeHistoryPartitions(primary: unknown, extra: unknown): unknown {
  // Neither response has to be a list: a single-turn read answers with an object
  // and a failed read with an ``{ error }`` envelope, and the union below can
  // carry neither. When the token partition holds nothing of its own, the
  // display-name response IS the answer — handing back ``primary`` would discard
  // the legacy turn this second request was made to find (TR-361).
  if (!Array.isArray(primary) || !Array.isArray(extra)) {
    if (carriesHistory(primary) || !carriesHistory(extra)) return primary;
    return extra;
  }
  if (extra.length === 0) return primary;
  const seen = new Set<string>();
  for (const item of primary) {
    const id = historyRowIdentity(item);
    if (id) seen.add(id);
  }
  const merged = [...primary];
  for (const item of extra) {
    const id = historyRowIdentity(item);
    if (id && seen.has(id)) continue;
    if (id) seen.add(id);
    merged.push(item);
  }
  return sortHistoryRowsByEpoch(merged);
}

function shouldReadLegacyHistoryPartition(body: Record<string, unknown>): boolean {
  // Single-item poll is already addressed by ``chatKey``; do not fan out.
  if (typeof body.chatKey === 'string' && body.chatKey.trim() !== '') return false;
  return shouldReadLegacyDisplayNamePartition(
    historyBodyCaseId(body),
    historyBodyCaseFolderName(body),
  );
}

/** True when the body addresses one turn by epoch instead of asking for a thread. */
function addressesOneTurn(body: Record<string, unknown>): boolean {
  const raw = body.epochTimeSecs;
  const epoch = typeof raw === 'string' ? Number.parseFloat(raw) : Number(raw);
  return Number.isFinite(epoch) && epoch > 0;
}

/** A miss, in either shape the service uses for a single-turn read. */
function isHistoryMiss(response: unknown): boolean {
  if (Array.isArray(response)) return response.length === 0;
  return response === null || response === undefined;
}

/**
 * A 200 body that reports a failed read instead of a turn.
 *
 * ``assistantRequest`` throws on a non-2xx status, so this envelope is the only
 * way a failed read reaches a caller — and the chat loader already branches on it
 * (``loadChatHistory`` in ``useImmigrationApplicantChat``). It carries no turn, so
 * it must not stand in front of the display-name partition; it must also never
 * *replace* a clean empty result, which would turn "no history" into "history
 * error" for that loader (TR-361).
 */
function isHistoryErrorEnvelope(response: unknown): boolean {
  return (
    typeof response === 'object' &&
    response !== null &&
    !Array.isArray(response) &&
    'error' in response
  );
}

/** True when a response carries a turn the caller can actually render. */
function carriesHistory(response: unknown): boolean {
  return !isHistoryMiss(response) && !isHistoryErrorEnvelope(response);
}

/**
 * Normalise a fanned-out result to the list shape the requesting caller reads.
 *
 * Only a body WITHOUT ``chatKey`` reaches the fan-out at all
 * (``shouldReadLegacyHistoryPartition``), and every such caller indexes the
 * response as a list: ``history[0]`` in the answer-row poll
 * (``components/answer-item.tsx``) and ``Array.isArray`` then ``rows.find`` in the
 * pending-turn reconcile (``components/chat.tsx``). The callers that read a bare
 * object instead (``app/main.tsx``, ``components/eventModal.tsx``, the quick-translate
 * poll, ``displayTranslate``) all pass ``chatKey``, so they never arrive here.
 *
 * The display-name partition answers a single-turn read with that bare object, so
 * returning it unwrapped read as "no turn" to the two list callers: keeping the
 * legacy row (TR-361) fixed the drop inside this module but the row still died at
 * the seam, and enrichment/translation polling spun to its timeout exactly as
 * before. Wrapping preserves the shape the *identical* request already got from
 * the token partition, which is what those callers were written against.
 *
 * A miss and an ``{ error }`` envelope are deliberately NOT wrapped: loaders branch
 * on ``error`` by key (``loadChatHistory`` in ``useImmigrationApplicantChat``,
 * ``components/chat.tsx``), and a one-row list holding an error envelope would read
 * as a turn and be mapped as one.
 */
function asHistoryList(response: unknown): unknown {
  if (Array.isArray(response) || !carriesHistory(response)) return response;
  return [response];
}

export async function fetchChatHistory(args: ChatHistoryMutationArgs): Promise<unknown> {
  const primary = await assistantRequest({
    ...args,
    path: '/chat/history',
    method: 'POST',
    body: args.body,
  });
  if (!shouldReadLegacyHistoryPartition(args.body)) return primary;
  // An ``epochTimeSecs`` read addresses exactly one turn, so a hit in the
  // folder-token partition is the answer — the display-name partition cannot hold a
  // better one, and reading it anyway costs one extra request per poll per open turn
  // on every renamed case. Only fall back when the token partition produced no
  // usable turn, which is how a pre-TR-359 turn presents (TR-361). An ``{ error }``
  // envelope counts: it carries no turn either, and ``mergeHistoryPartitions`` hands
  // it back untouched if the display-name read has nothing better.
  if (addressesOneTurn(args.body) && carriesHistory(primary)) return primary;
  try {
    const extra = await assistantRequest({
      ...args,
      path: '/chat/history',
      method: 'POST',
      body: withoutCaseFolderName(args.body),
    });
    return asHistoryList(mergeHistoryPartitions(primary, extra));
  } catch {
    return primary;
  }
}

export async function deleteChatHistory(args: ChatHistoryMutationArgs): Promise<void> {
  await assistantRequest({
    ...args,
    path: '/chat/history',
    method: 'DELETE',
    body: args.body,
  });
  if (!shouldReadLegacyHistoryPartition(args.body)) return;
  try {
    await assistantRequest({
      ...args,
      path: '/chat/history',
      method: 'DELETE',
      body: withoutCaseFolderName(args.body),
    });
  } catch {
    // The token partition was already cleared; a missing name-key is not a failure.
  }
}

export type SignChatAttachmentArgs = AssistantSessionArgs & {
  s3Key: string;
  signal?: AbortSignal;
};

/** Mint a 5-minute presigned download URL for a chat attachment via assistant-service. */
export async function signChatAttachment(
  args: SignChatAttachmentArgs,
): Promise<{ signed_url: string }> {
  return assistantRequest<{ signed_url: string }>({
    ...args,
    path: '/chat/attachments/sign',
    method: 'POST',
    body: { s3_key: args.s3Key },
  });
}

/**
 * PUT `/chat/history` to restore ``sessionId`` and archive ``deleteSessionId``.
 *
 * The picker now lists sessions from both partitions after a rename
 * ({@link fetchChatSessionsFromArchive}), so the chosen session may still live
 * under the pre-TR-359 ``{display name}-{userId}`` key while ``caseFolderName``
 * names the folder-token partition. Keyed only on the folder name, that restore
 * matches nothing while the archive of the current session still lands — the
 * user loses the open thread and gets no thread back (TR-361).
 *
 * So when a legacy partition can exist and the folder-name-keyed PUT restores
 * nothing, retry against the display-name partition. The retry carries the same
 * ``deleteSessionId``: the first call already archived it in the folder-token
 * partition, and repeating it here is a no-op unless the open thread was itself
 * a restored display-name session — in which case archiving it is the point.
 */
export async function swapChatSession(args: ChatHistoryMutationArgs): Promise<{ sessionId?: string | null }> {
  const swap = (body: Record<string, unknown>) =>
    assistantRequest<{ sessionId?: string | null }>({
      ...args,
      path: '/chat/history',
      method: 'PUT',
      body,
    });
  const hasLegacyPartition = shouldReadLegacyDisplayNamePartition(
    historyBodyCaseId(args.body),
    historyBodyCaseFolderName(args.body),
  );
  if (!hasLegacyPartition) return swap(args.body);
  // A miss can surface either as an empty session id or as a 404; both mean the
  // target was not in the folder-token partition. Any other status is a real
  // failure and must not be retried against a second partition.
  const restored = await swap(args.body).catch((error: unknown) => {
    if (error instanceof AssistantApiError && error.status === 404) return null;
    throw error;
  });
  if (restored?.sessionId) return restored;
  return swap(withoutCaseFolderName(args.body));
}

export type ChatArchiveSessionsBody = {
  caseId: string;
  userId: string;
  /**
   * The S3 folder name for ``caseId`` — rename-safe. Drives the DynamoDB partition
   * on the service side and the row filter here, so both stay reachable across a
   * rename. Omit for a legacy case (its folder name IS its display name).
   */
  caseFolderName?: string;
};

export type FetchChatSessionsFromArchiveArgs = AssistantSessionArgs & {
  body: ChatArchiveSessionsBody;
  signal?: AbortSignal;
};

async function requestArchiveItems(
  args: FetchChatSessionsFromArchiveArgs,
  body: { caseId: string; userId: string; caseFolderName?: string },
): Promise<unknown> {
  return assistantRequest<unknown>({
    ...args,
    path: '/chat/history/archive',
    method: 'POST',
    body,
  });
}

function assertArchiveItems(items: unknown): unknown[] {
  if (!Array.isArray(items)) {
    throw new AssistantApiError(
      'assistant-service /chat/history/archive returned a non-array body.',
      null,
      null,
    );
  }
  return items;
}

/** POST `/chat/history/archive` and map rows to the same `sessions` shape as the legacy BFF. */
export async function fetchChatSessionsFromArchive(
  args: FetchChatSessionsFromArchiveArgs,
): Promise<{ sessions: Record<string, Record<string, unknown>> }> {
  const { caseId, userId, caseFolderName } = args.body;
  const primaryBody = { caseId, userId, ...(caseFolderName ? { caseFolderName } : {}) };
  // Assert *after* the display-name read below, not on the way in: an unusable
  // token-partition body must not pre-empt the only request that can reach a
  // pre-TR-359 session list for a folder-token case (TR-361).
  const primary = await requestArchiveItems(args, primaryBody);
  let combined: unknown = primary;
  if (shouldReadLegacyDisplayNamePartition(caseId, caseFolderName)) {
    try {
      const extra = await requestArchiveItems(args, { caseId, userId });
      if (Array.isArray(primary) && Array.isArray(extra)) {
        combined = mergeHistoryPartitions(primary, extra);
      } else if (Array.isArray(extra) && extra.length > 0) {
        // The token-partition body was not a list, so its sessions are unreachable
        // either way. The display-name rows are a real list and are all a case
        // written before the rename has, so list them rather than failing the
        // whole picker. An empty legacy list adds nothing, so let the assert below
        // surface the broken response instead of showing an empty archive.
        combined = extra;
      }
    } catch {
      // Token partition already loaded; a missing name-key is not a failure.
    }
  }
  const items = assertArchiveItems(combined);
  // Prefer the rename-stable folder name, but also keep pre-token rows keyed on
  // the current display name (``caseId``) so auto/gated history written as
  // ``{name}-{userId}`` still appears after this deploy (TR-359).
  const folderName = caseFolderName?.trim() || caseId;
  const sessions = buildSessionsFromHistoryItems(items, folderName, userId, caseId);
  return { sessions };
}

export const assistantKeys = {
  all: ['assistant'] as const,
  chat: (cellId: string | null) => [...assistantKeys.all, 'chat', cellId] as const,
  history: (cellId: string | null, caseId: string | null) =>
    [...assistantKeys.all, 'history', cellId, caseId] as const,
};
